import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { ChatInput } from '@client/components/entity-chat/ChatInput';
import { ChatMessage } from '@client/components/entity-chat/ChatMessage';
import { useChatScrollAnchor } from '@client/components/entity-chat/useChatScrollAnchor';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useThemeStore } from '@client/stores/themeStore';
import { AI_TOOL_NAMES, type AiToolName } from '@shared/types/ai/tools';
import { createFileRoute } from '@tanstack/react-router';
import { Badge, Card, Collapse, Divider, Empty, Select, Statistic, Switch, Tag, Tooltip, Typography } from 'antd';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import {
    AlertTriangle,
    Bot,
    CheckCircle2,
    ChevronRight,
    Clock,
    Compass,
    FileText,
    FolderOpen,
    Hash,
    HelpCircle,
    History,
    Lightbulb,
    Link2,
    Mail,
    Moon,
    Play,
    Search,
    Settings,
    Sun,
    Target,
    Trash2,
    User,
    Wrench,
    Zap,
} from 'lucide-react';
import { type FormEvent, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

const { Title, Text, Paragraph } = Typography;

export const Route = createFileRoute('/console/$orgId/component-showcase/ai-chat-test')({
    staticData: {
        breadcrumb: { title: 'AI Chat Test' },
        featureFlag: 'enable-debug-info',
    },
    component: AiChatTestShowcase,
});

// Define Message type locally
type Message = {
    id: string;
    role: 'user' | 'assistant' | 'system';
    content: string;
    toolCalls?: Array<{
        name: string;
        args: unknown;
        result?: unknown;
    }>;
    timestamp?: number;
};

// Tool descriptions for reference
const TOOL_DESCRIPTIONS: Record<
    string,
    {
        name: string;
        description: string;
        parameters: string;
        category: 'search' | 'navigation' | 'entity' | 'action' | 'help';
        color: string;
    }
> = {
    [AI_TOOL_NAMES.SEARCH_ENTITIES]: {
        name: 'searchEntities',
        description: 'Search across complaints, claims, incidents, and obligations with security trimming',
        parameters: 'query, entityTypes?, filters?, limit?',
        category: 'search',
        color: 'blue',
    },
    [AI_TOOL_NAMES.GET_NAVIGATION_LINKS]: {
        name: 'getNavigationLinks',
        description: 'Find pages and features within the platform based on user intent',
        parameters: 'query, actionType?, category?',
        category: 'navigation',
        color: 'cyan',
    },
    [AI_TOOL_NAMES.GET_SYSTEM_HELP]: {
        name: 'getSystemHelp',
        description: 'Get help documentation about platform features and workflows',
        parameters: 'topic?, query?',
        category: 'help',
        color: 'green',
    },
    [AI_TOOL_NAMES.SUGGEST_NEXT_STEPS]: {
        name: 'suggestNextSteps',
        description: 'Suggest next actions based on entity state and workflow',
        parameters: 'entityType, entityId, focusArea?',
        category: 'help',
        color: 'orange',
    },
    [AI_TOOL_NAMES.LOAD_ENTITY]: {
        name: 'loadEntity',
        description: 'Get detailed information about a specific record',
        parameters: 'entityType, entityId',
        category: 'entity',
        color: 'purple',
    },
    [AI_TOOL_NAMES.LOAD_ENTITY_HISTORY]: {
        name: 'loadEntityHistory',
        description: 'View the change history and audit trail of a record',
        parameters: 'entityType, entityId, limit?',
        category: 'entity',
        color: 'geekblue',
    },
    [AI_TOOL_NAMES.LOAD_ENTITY_FILES]: {
        name: 'loadEntityFiles',
        description: 'List files and attachments associated with a record',
        parameters: 'entityType, entityId',
        category: 'entity',
        color: 'volcano',
    },
    [AI_TOOL_NAMES.LOAD_ENTITY_TIMELINE]: {
        name: 'loadEntityTimeline',
        description: 'Get the timeline of events for a record',
        parameters: 'entityType, entityId',
        category: 'entity',
        color: 'gold',
    },
    [AI_TOOL_NAMES.LOAD_ENTITY_COMMUNICATION]: {
        name: 'loadEntityCommunication',
        description: 'View emails and communications linked to a record',
        parameters: 'entityType, entityId',
        category: 'entity',
        color: 'lime',
    },
    [AI_TOOL_NAMES.MATCH_INCIDENT_TO_OBLIGATIONS]: {
        name: 'matchIncidentToObligations',
        description: 'Find regulatory obligations that may apply to an incident',
        parameters: 'incidentId, threshold?',
        category: 'search',
        color: 'magenta',
    },
    [AI_TOOL_NAMES.CREATE_TASK]: {
        name: 'createTask',
        description: 'Create a task for a user',
        parameters: 'title, description?, assigneeId?, dueDate?',
        category: 'action',
        color: 'red',
    },
};

// Predefined test queries organised by tool category
const TEST_QUERIES = {
    search: {
        title: 'Search Entities',
        icon: Search,
        color: 'blue',
        description: 'Find records across the platform',
        queries: [
            {
                label: 'Open incidents this month',
                query: 'Find all open incidents from the last month',
                expectedTool: 'searchEntities',
            },
            {
                label: 'Pending complaints',
                query: 'Show me all pending complaints',
                expectedTool: 'searchEntities',
            },
            {
                label: 'Property damage claims',
                query: 'Find claims related to property damage',
                expectedTool: 'searchEntities',
            },
            {
                label: 'Obligations due soon',
                query: 'What obligations are due this week?',
                expectedTool: 'searchEntities',
            },
            {
                label: 'Cyber security records',
                query: 'Search for anything mentioning cyber security',
                expectedTool: 'searchEntities',
            },
            {
                label: 'High severity incidents',
                query: 'Find all high severity incidents',
                expectedTool: 'searchEntities',
            },
        ],
    },
    navigation: {
        title: 'Navigation',
        icon: Compass,
        color: 'cyan',
        description: 'Find pages and features',
        queries: [
            {
                label: 'Add new incident',
                query: 'Where can I log a new incident?',
                expectedTool: 'getNavigationLinks',
            },
            {
                label: 'Create complaint',
                query: 'How do I add a new complaint?',
                expectedTool: 'getNavigationLinks',
            },
            {
                label: 'View dashboard',
                query: 'Navigate to the dashboard',
                expectedTool: 'getNavigationLinks',
            },
            {
                label: 'Manage obligations',
                query: 'Where can I manage regulatory obligations?',
                expectedTool: 'getNavigationLinks',
            },
            {
                label: 'User settings',
                query: 'Where are my account settings?',
                expectedTool: 'getNavigationLinks',
            },
            {
                label: 'Reports section',
                query: 'Navigate to reports',
                expectedTool: 'getNavigationLinks',
            },
        ],
    },
    help: {
        title: 'System Help',
        icon: HelpCircle,
        color: 'green',
        description: 'Get help with platform features',
        queries: [
            {
                label: 'Logging incidents',
                query: 'How do I log a new incident?',
                expectedTool: 'getSystemHelp',
            },
            {
                label: 'Complaint workflow',
                query: 'What is the process for handling complaints?',
                expectedTool: 'getSystemHelp',
            },
            {
                label: 'Obligation tracking',
                query: 'How do I track regulatory obligations?',
                expectedTool: 'getSystemHelp',
            },
            {
                label: 'Claims process',
                query: 'Walk me through the claims process',
                expectedTool: 'getSystemHelp',
            },
            {
                label: 'User management',
                query: 'How do I manage users in my organisation?',
                expectedTool: 'getSystemHelp',
            },
            {
                label: 'Password reset',
                query: 'How to reset an employees password?',
                expectedTool: 'getSystemHelp',
            },
        ],
    },
    nextSteps: {
        title: 'Next Steps',
        icon: Lightbulb,
        color: 'orange',
        description: 'Get guidance on what to do next',
        queries: [
            {
                label: 'Incident guidance',
                query: 'What should I do next with incident #1?',
                expectedTool: 'suggestNextSteps',
            },
            {
                label: 'Claim progress',
                query: 'What are the next steps for claim #1?',
                expectedTool: 'suggestNextSteps',
            },
            {
                label: 'Complaint resolution',
                query: 'How should I proceed with complaint #1?',
                expectedTool: 'suggestNextSteps',
            },
            {
                label: 'Compliance actions',
                query: 'What compliance actions are needed for obligation #1?',
                expectedTool: 'suggestNextSteps',
            },
        ],
    },
    entityDetails: {
        title: 'Entity Details',
        icon: FileText,
        color: 'purple',
        description: 'Load detailed record information',
        queries: [
            {
                label: 'Incident details',
                query: 'Tell me about incident #1',
                expectedTool: 'loadEntity',
            },
            {
                label: 'Claim information',
                query: 'Show me the details of claim #1',
                expectedTool: 'loadEntity',
            },
            {
                label: 'Complaint overview',
                query: 'What is complaint #1 about?',
                expectedTool: 'loadEntity',
            },
            {
                label: 'Obligation requirements',
                query: 'Show me obligation #1 details',
                expectedTool: 'loadEntity',
            },
        ],
    },
    history: {
        title: 'History & Timeline',
        icon: History,
        color: 'gold',
        description: 'View record history and timeline',
        queries: [
            {
                label: 'Incident history',
                query: 'Show me the change history for incident #1',
                expectedTool: 'loadEntityHistory',
            },
            {
                label: 'Claim timeline',
                query: 'What is the timeline for claim #1?',
                expectedTool: 'loadEntityTimeline',
            },
            {
                label: 'Complaint audit',
                query: 'Show me who made changes to complaint #1',
                expectedTool: 'loadEntityHistory',
            },
            {
                label: 'Obligation events',
                query: 'Show the event timeline for obligation #1',
                expectedTool: 'loadEntityTimeline',
            },
        ],
    },
    files: {
        title: 'Files & Documents',
        icon: FolderOpen,
        color: 'volcano',
        description: 'View attachments and documents',
        queries: [
            {
                label: 'Incident files',
                query: 'What files are attached to incident #1?',
                expectedTool: 'loadEntityFiles',
            },
            {
                label: 'Claim documents',
                query: 'Show me documents for claim #1',
                expectedTool: 'loadEntityFiles',
            },
            {
                label: 'Complaint attachments',
                query: 'List attachments for complaint #1',
                expectedTool: 'loadEntityFiles',
            },
        ],
    },
    communication: {
        title: 'Communication',
        icon: Mail,
        color: 'lime',
        description: 'View emails and messages',
        queries: [
            {
                label: 'Incident emails',
                query: 'Show me emails related to incident #1',
                expectedTool: 'loadEntityCommunication',
            },
            {
                label: 'Claim correspondence',
                query: 'What communications are linked to claim #1?',
                expectedTool: 'loadEntityCommunication',
            },
            {
                label: 'Complaint messages',
                query: 'Show all messages for complaint #1',
                expectedTool: 'loadEntityCommunication',
            },
        ],
    },
    compliance: {
        title: 'Compliance',
        icon: Target,
        color: 'magenta',
        description: 'Compliance matching and analysis',
        queries: [
            {
                label: 'Match obligations',
                query: 'What obligations might apply to incident #1?',
                expectedTool: 'matchIncidentToObligations',
            },
            {
                label: 'Regulatory impact',
                query: 'Which regulations are relevant to incident #2?',
                expectedTool: 'matchIncidentToObligations',
            },
            {
                label: 'Breach obligations',
                query: 'Find obligations related to this data breach incident',
                expectedTool: 'matchIncidentToObligations',
            },
        ],
    },
    actions: {
        title: 'Actions',
        icon: Zap,
        color: 'red',
        description: 'Create and modify records',
        queries: [
            {
                label: 'Create review task',
                query: 'Create a task to review the latest incidents',
                expectedTool: 'createTask',
            },
            {
                label: 'Assign follow-up',
                query: 'Create a follow-up task for incident #1',
                expectedTool: 'createTask',
            },
        ],
    },
};

type QueryCategory = keyof typeof TEST_QUERIES;

interface TestQuery {
    label: string;
    query: string;
    expectedTool: string;
}

function TestQueryList({
    queries,
    onSelectQuery,
    color,
}: {
    queries: TestQuery[];
    onSelectQuery: (query: string) => void;
    color: string;
}) {
    return (
        <div className="space-y-1.5">
            {queries.map((q) => (
                <button
                    key={q.label}
                    type="button"
                    className="group flex w-full cursor-pointer items-start gap-2 rounded-lg border border-separator bg-card p-2.5 text-left transition-all hover:border-accent-300 hover:bg-accent-50 hover:shadow-sm"
                    onClick={() => onSelectQuery(q.query)}
                >
                    <Play
                        size={14}
                        className="mt-0.5 flex-shrink-0 text-quaternary-foreground transition-colors group-hover:text-accent-500"
                    />
                    <div className="min-w-0 flex-1">
                        <Text className="block text-foreground text-sm leading-snug">{q.query}</Text>
                        <div className="mt-1 flex items-center gap-2">
                            <Tag color={color} className="!m-0 !text-[10px]">
                                {q.expectedTool}
                            </Tag>
                            <Text className="text-[10px] text-subtle-foreground">{q.label}</Text>
                        </div>
                    </div>
                </button>
            ))}
        </div>
    );
}

function ToolCallsDebugPanel({
    toolCalls,
    filter,
    onFilterChange,
}: {
    toolCalls: Array<{ name: string; args: unknown; result?: unknown; timestamp?: number }>;
    filter: string;
    onFilterChange: (value: string) => void;
}) {
    const filteredCalls = useMemo(() => {
        if (!filter || filter === 'all') return toolCalls;
        return toolCalls.filter((tc) => tc.name === filter);
    }, [toolCalls, filter]);

    const uniqueTools = useMemo(() => {
        const tools = new Set(toolCalls.map((tc) => tc.name));
        return Array.from(tools);
    }, [toolCalls]);

    if (toolCalls.length === 0) {
        return (
            <Empty
                image={<Wrench size={40} className="mx-auto text-quaternary-foreground" />}
                description={
                    <div className="space-y-1">
                        <Text className="text-muted-foreground text-sm">No tool calls yet</Text>
                        <Text className="text-subtle-foreground text-xs">
                            Tool calls will appear here when the AI uses tools.
                        </Text>
                    </div>
                }
            />
        );
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <Text strong className="text-sm">
                    Tool Calls ({filteredCalls.length})
                </Text>
                <Select
                    size="small"
                    value={filter}
                    onChange={onFilterChange}
                    className="w-40"
                    options={[
                        { label: 'All Tools', value: 'all' },
                        ...uniqueTools.map((t) => ({ label: t, value: t })),
                    ]}
                />
            </div>

            <div className="space-y-2">
                {filteredCalls.map((tc, index) => {
                    const toolConfig = TOOL_DESCRIPTIONS[tc.name];
                    return (
                        <Collapse
                            key={index}
                            size="small"
                            items={[
                                {
                                    key: index.toString(),
                                    label: (
                                        <div className="flex items-center gap-2">
                                            <Tag color={toolConfig?.color ?? 'default'}>{tc.name}</Tag>
                                            {tc.result ? (
                                                <Tag color="green" icon={<CheckCircle2 size={10} />}>
                                                    Completed
                                                </Tag>
                                            ) : (
                                                <Tag color="orange" icon={<Clock size={10} />}>
                                                    In Progress
                                                </Tag>
                                            )}
                                        </div>
                                    ),
                                    children: (
                                        <div className="space-y-2">
                                            <div>
                                                <Text className="font-medium text-[10px] text-muted-foreground">
                                                    Arguments:
                                                </Text>
                                                <pre className="mt-1 max-h-24 overflow-auto rounded bg-muted p-2 text-[10px]">
                                                    {JSON.stringify(tc.args, null, 2)}
                                                </pre>
                                            </div>
                                            {tc.result !== undefined && (
                                                <div>
                                                    <Text className="font-medium text-[10px] text-muted-foreground">
                                                        Result:
                                                    </Text>
                                                    <pre className="mt-1 max-h-32 overflow-auto rounded bg-muted p-2 text-[10px]">
                                                        {JSON.stringify(tc.result, null, 2)}
                                                    </pre>
                                                </div>
                                            )}
                                        </div>
                                    ),
                                },
                            ]}
                        />
                    );
                })}
            </div>
        </div>
    );
}

function StatsPanel({
    messages,
    toolCalls,
}: {
    messages: Message[];
    toolCalls: Array<{ name: string; args: unknown; result?: unknown }>;
}) {
    const stats = useMemo(() => {
        const userMessages = messages.filter((m) => m.role === 'user').length;
        const assistantMessages = messages.filter((m) => m.role === 'assistant').length;
        const completedTools = toolCalls.filter((tc) => tc.result !== undefined).length;
        const toolsByName = toolCalls.reduce(
            (acc, tc) => {
                acc[tc.name] = (acc[tc.name] || 0) + 1;
                return acc;
            },
            {} as Record<string, number>,
        );

        return {
            userMessages,
            assistantMessages,
            totalTools: toolCalls.length,
            completedTools,
            toolsByName,
        };
    }, [messages, toolCalls]);

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
                <Statistic
                    title={<span className="text-xs">Messages</span>}
                    value={stats.userMessages + stats.assistantMessages}
                    valueStyle={{ fontSize: '20px' }}
                    prefix={<Hash size={14} />}
                />
                <Statistic
                    title={<span className="text-xs">Tool Calls</span>}
                    value={stats.totalTools}
                    valueStyle={{ fontSize: '20px' }}
                    prefix={<Wrench size={14} />}
                    suffix={
                        stats.completedTools > 0 && (
                            <span className="text-status-success text-xs">({stats.completedTools} done)</span>
                        )
                    }
                />
            </div>

            {Object.keys(stats.toolsByName).length > 0 && (
                <div>
                    <Text className="mb-2 block font-medium text-muted-foreground text-xs">Tools Used</Text>
                    <div className="flex flex-wrap gap-1">
                        {Object.entries(stats.toolsByName).map(([name, count]) => (
                            <Badge key={name} count={count} size="small" color={TOOL_DESCRIPTIONS[name]?.color}>
                                <Tag className="!m-0">{name}</Tag>
                            </Badge>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function ChatTestSection() {
    const { trpc } = useTrpc();
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [allToolCalls, setAllToolCalls] = useState<
        Array<{ name: string; args: unknown; result?: unknown; timestamp?: number }>
    >([]);
    const [toolFilter, setToolFilter] = useState('all');
    const [activeQueryTab, setActiveQueryTab] = useState<string>('search');
    const abortControllerRef = useRef<AbortController | null>(null);
    const inputRef = useRef<TextAreaRef>(null);
    const chatSessionIdRef = useRef(uuidv4());
    const { scrollContainerRef, registerMessageElement } = useChatScrollAnchor(messages);

    const streamChatMutation = trpc.aiChat.streamGeneralChat.useMutation();

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setInput(e.target.value);
    };

    const handleSelectQuery = (query: string) => {
        setInput(query);
        setTimeout(() => {
            inputRef.current?.focus();
        }, 0);
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!input.trim() || isLoading) return;

        const userMessage: Message = {
            id: uuidv4(),
            role: 'user',
            content: input.trim(),
            timestamp: Date.now(),
        };

        const assistantMessage: Message = {
            id: uuidv4(),
            role: 'assistant',
            content: '',
            timestamp: Date.now(),
        };

        setMessages((prev) => [...prev, userMessage, assistantMessage]);
        setInput('');
        setIsLoading(true);

        setTimeout(() => {
            inputRef.current?.focus();
        }, 0);

        try {
            abortControllerRef.current = new AbortController();

            const response = await streamChatMutation.mutateAsync({
                messages: [...messages, userMessage].map((m) => ({
                    role: m.role as 'user' | 'assistant' | 'system',
                    content: m.content,
                })),
                chatSessionId: chatSessionIdRef.current,
            });

            for await (const chunk of response) {
                if (abortControllerRef.current?.signal.aborted) break;

                if (chunk.type === 'text') {
                    setMessages((prev) => {
                        const newMessages = [...prev];
                        const lastMessage = newMessages[newMessages.length - 1];
                        if (lastMessage && lastMessage.role === 'assistant') {
                            lastMessage.content += chunk.content;
                        }
                        return newMessages;
                    });
                } else if (chunk.type === 'tool-call') {
                    const toolName = chunk.toolName as AiToolName;
                    const newToolCall = {
                        name: toolName,
                        args: chunk.args,
                        timestamp: Date.now(),
                    };
                    setAllToolCalls((prev) => [...prev, newToolCall]);

                    setMessages((prev) => {
                        const newMessages = [...prev];
                        const lastMessage = newMessages[newMessages.length - 1];
                        if (lastMessage && lastMessage.role === 'assistant') {
                            if (!lastMessage.toolCalls) {
                                lastMessage.toolCalls = [];
                            }
                            lastMessage.toolCalls.push(newToolCall);
                        }
                        return newMessages;
                    });
                } else if (chunk.type === 'tool-result') {
                    setAllToolCalls((prev) => {
                        const newCalls = [...prev];
                        const lastCall = newCalls.findLast((tc) => tc.name === chunk.toolName);
                        if (lastCall) {
                            lastCall.result = chunk.result;
                        }
                        return newCalls;
                    });

                    setMessages((prev) => {
                        const newMessages = [...prev];
                        const lastMessage = newMessages[newMessages.length - 1];
                        if (lastMessage && lastMessage.role === 'assistant' && lastMessage.toolCalls) {
                            const toolCall = lastMessage.toolCalls.find(
                                (tc) => tc.name === (chunk.toolName || 'unknown'),
                            );
                            if (toolCall) {
                                toolCall.result = chunk.result;
                            }
                        }
                        return newMessages;
                    });
                }
            }
        } catch (error) {
            errorMessage.show(error);
            setMessages((prev) => prev.slice(0, -1));
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        } finally {
            setIsLoading(false);
            abortControllerRef.current = null;
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        }
    };

    const handleClearChat = () => {
        setMessages([]);
        setAllToolCalls([]);
        setInput('');
        setToolFilter('all');
        chatSessionIdRef.current = uuidv4();
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
    };

    const activeConfig = TEST_QUERIES[activeQueryTab as QueryCategory];

    return (
        <div className="grid grid-cols-12 gap-4">
            {/* Test Queries Panel - Left */}
            <div className="col-span-3">
                <Card
                    title={
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Settings size={16} className="text-muted-foreground" />
                                <span>Test Queries</span>
                            </div>
                            <Tag className="!m-0">{activeConfig?.queries.length ?? 0}</Tag>
                        </div>
                    }
                    size="small"
                    className="h-[700px]"
                    styles={{ body: { height: 'calc(100% - 46px)', overflow: 'hidden', padding: 0 } }}
                >
                    <div className="flex h-full flex-col">
                        {/* Category Selector */}
                        <div className="border-separator border-b p-3">
                            <Select
                                value={activeQueryTab}
                                onChange={setActiveQueryTab}
                                className="w-full"
                                size="middle"
                                options={Object.entries(TEST_QUERIES).map(([key, config]) => ({
                                    value: key,
                                    label: (
                                        <div className="flex items-center gap-2">
                                            <config.icon size={14} className={`text-${config.color}-500`} />
                                            <span>{config.title}</span>
                                            <span className="ml-auto text-muted-foreground text-xs">
                                                {config.queries.length}
                                            </span>
                                        </div>
                                    ),
                                }))}
                            />
                            {activeConfig && (
                                <Text className="mt-2 block text-muted-foreground text-xs">
                                    {activeConfig.description}
                                </Text>
                            )}
                        </div>

                        {/* Query List */}
                        <div className="flex-1 overflow-y-auto p-3">
                            {activeConfig && (
                                <TestQueryList
                                    queries={activeConfig.queries}
                                    onSelectQuery={handleSelectQuery}
                                    color={activeConfig.color}
                                />
                            )}
                        </div>
                    </div>
                </Card>
            </div>

            {/* Chat Panel - Center */}
            <div className="col-span-5">
                <Card
                    title={
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Bot size={16} className="text-accent-500" />
                                <span>AI Chat</span>
                                {messages.length > 0 && (
                                    <Tag color="blue" className="!m-0">
                                        {messages.length} messages
                                    </Tag>
                                )}
                            </div>
                            <Button
                                type="tertiary"
                                size="small"
                                icon={<Trash2 size={14} />}
                                onClick={handleClearChat}
                                disabled={messages.length === 0}
                            >
                                Clear
                            </Button>
                        </div>
                    }
                    className="h-[700px]"
                    styles={{
                        body: {
                            padding: 0,
                            height: 'calc(100% - 57px)',
                            display: 'flex',
                            flexDirection: 'column',
                        },
                    }}
                >
                    {/* Messages Container */}
                    <div ref={scrollContainerRef} className="flex-1 space-y-4 overflow-y-auto p-4">
                        {messages.length === 0 && (
                            <div className="mt-8 text-center text-subtle-foreground">
                                <Bot size={48} className="mx-auto mb-4 text-quaternary-foreground" />
                                <p className="text-sm">
                                    Select a test query from the left panel
                                    <br />
                                    or type your own query below.
                                </p>
                                <div className="mx-auto mt-4 max-w-xs space-y-2 text-left">
                                    <div className="flex items-center gap-2 text-xs">
                                        <ChevronRight size={12} className="text-accent-500" />
                                        <span>Click any query to load it</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs">
                                        <ChevronRight size={12} className="text-accent-500" />
                                        <span>Press Enter to send</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs">
                                        <ChevronRight size={12} className="text-accent-500" />
                                        <span>Tool calls appear on the right</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {messages.map((message) => (
                            <div key={message.id} ref={registerMessageElement(message.id)}>
                                <ChatMessage
                                    message={message}
                                    isLoading={isLoading && message.role === 'assistant' && message.content === ''}
                                />
                            </div>
                        ))}
                    </div>

                    {/* Input Container */}
                    <ChatInput
                        ref={inputRef}
                        input={input}
                        isLoading={isLoading}
                        onInputChange={handleInputChange}
                        onSubmit={handleSubmit}
                    />
                </Card>
            </div>

            {/* Debug Panel - Right */}
            <div className="col-span-4 space-y-4">
                {/* Stats Card */}
                <Card
                    title={
                        <div className="flex items-center gap-2">
                            <Zap size={16} className="text-gold-500" />
                            <span>Session Stats</span>
                        </div>
                    }
                    size="small"
                >
                    <StatsPanel messages={messages} toolCalls={allToolCalls} />
                </Card>

                {/* Tool Calls Debug */}
                <Card
                    title={
                        <div className="flex items-center gap-2">
                            <Wrench size={16} className="text-orange-500" />
                            <span>Tool Calls Debug</span>
                        </div>
                    }
                    size="small"
                    className="h-[470px]"
                    styles={{ body: { height: 'calc(100% - 46px)', overflow: 'auto' } }}
                >
                    <ToolCallsDebugPanel toolCalls={allToolCalls} filter={toolFilter} onFilterChange={setToolFilter} />
                </Card>
            </div>
        </div>
    );
}

function ApiReferenceSection() {
    const toolCategories = {
        search: { label: 'Search & Discovery', color: 'blue', icon: Search },
        navigation: { label: 'Navigation', color: 'cyan', icon: Compass },
        entity: { label: 'Entity Operations', color: 'purple', icon: FileText },
        action: { label: 'Data Modifying', color: 'red', icon: Zap },
        help: { label: 'Help & Guidance', color: 'green', icon: HelpCircle },
    };

    const toolsByCategory = Object.entries(TOOL_DESCRIPTIONS).reduce(
        (acc, [key, tool]) => {
            if (!acc[tool.category]) {
                acc[tool.category] = [];
            }
            acc[tool.category].push({ key, ...tool });
            return acc;
        },
        {} as Record<string, Array<(typeof TOOL_DESCRIPTIONS)[string] & { key: string }>>,
    );

    return (
        <Card
            title={
                <div className="flex items-center gap-3">
                    <Link2 size={20} className="text-accent-500" />
                    <div>
                        <Title level={4} className="!mb-0">
                            API Reference
                        </Title>
                        <Text className="text-muted-foreground text-sm">
                            {Object.keys(TOOL_DESCRIPTIONS).length} available AI tools
                        </Text>
                    </div>
                </div>
            }
        >
            <div className="grid grid-cols-2 gap-6">
                {Object.entries(toolCategories).map(([categoryKey, categoryConfig]) => {
                    const tools = toolsByCategory[categoryKey] || [];
                    const CategoryIcon = categoryConfig.icon;

                    return (
                        <div key={categoryKey}>
                            <div className="mb-3 flex items-center gap-2">
                                <CategoryIcon size={16} className={`text-${categoryConfig.color}-500`} />
                                <Text strong>{categoryConfig.label}</Text>
                                <Tag color={categoryConfig.color}>{tools.length}</Tag>
                            </div>
                            <div className="space-y-2">
                                {tools.map((tool) => (
                                    <div
                                        key={tool.key}
                                        className="rounded-lg border border-separator bg-card p-3 transition-colors hover:border-accent-200"
                                    >
                                        <div className="mb-1 flex items-center gap-2">
                                            <Tag color={tool.color} className="!m-0">
                                                {tool.name}
                                            </Tag>
                                            {tool.category === 'action' && (
                                                <Tooltip title="This tool modifies data">
                                                    <AlertTriangle size={12} className="text-status-warning" />
                                                </Tooltip>
                                            )}
                                        </div>
                                        <Text className="mb-1 block text-muted-foreground text-xs">
                                            {tool.description}
                                        </Text>
                                        <code className="text-[10px] text-subtle-foreground">{tool.parameters}</code>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>

            <Divider />

            <div className="grid grid-cols-2 gap-6">
                <div>
                    <Text strong className="mb-2 block">
                        Usage Notes
                    </Text>
                    <ul className="list-disc space-y-1 pl-5 text-muted-foreground text-xs">
                        <li>
                            <strong>Security Trimming:</strong> All search results are filtered based on user
                            permissions
                        </li>
                        <li>
                            <strong>Entity References:</strong> Use format "Entity Type #ID" (e.g., "Incident #42")
                        </li>
                        <li>
                            <strong>Multi-turn:</strong> The AI maintains context across messages in the conversation
                        </li>
                        <li>
                            <strong>Tool Chaining:</strong> The AI can use multiple tools to answer complex queries
                        </li>
                    </ul>
                </div>
                <div>
                    <Text strong className="mb-2 block">
                        Best Practices
                    </Text>
                    <ul className="list-disc space-y-1 pl-5 text-muted-foreground text-xs">
                        <li>
                            <strong>Be Specific:</strong> Include entity IDs when asking about specific records
                        </li>
                        <li>
                            <strong>Natural Language:</strong> Ask questions naturally - the AI will choose the right
                            tool
                        </li>
                        <li>
                            <strong>Context Aware:</strong> The AI remembers previous messages in the conversation
                        </li>
                        <li>
                            <strong>Error Handling:</strong> Check tool results in the debug panel for troubleshooting
                        </li>
                    </ul>
                </div>
            </div>
        </Card>
    );
}

function QuickTestGrid({ onSelectQuery }: { onSelectQuery: (query: string) => void }) {
    const quickTests = [
        { query: 'Find open incidents', icon: Search, color: 'blue' },
        { query: 'Where can I add a complaint?', icon: Compass, color: 'cyan' },
        { query: 'How do I handle complaints?', icon: HelpCircle, color: 'green' },
        { query: 'Next steps for incident #1', icon: Lightbulb, color: 'orange' },
        { query: 'Tell me about claim #1', icon: FileText, color: 'purple' },
        { query: 'Show history for incident #1', icon: History, color: 'gold' },
        { query: 'Files for claim #1', icon: FolderOpen, color: 'volcano' },
        { query: 'Create a review task', icon: Zap, color: 'red' },
    ];

    return (
        <div className="grid grid-cols-4 gap-2">
            {quickTests.map((test) => (
                <button
                    key={test.query}
                    type="button"
                    className={`flex items-center gap-2 rounded-lg border border-separator bg-card p-3 text-left transition-all hover:border-${test.color}-300 hover:bg-${test.color}-50`}
                    onClick={() => onSelectQuery(test.query)}
                >
                    <test.icon size={16} className={`text-${test.color}-500`} />
                    <span className="truncate text-foreground text-xs">{test.query}</span>
                </button>
            ))}
        </div>
    );
}

function AiChatTestShowcase() {
    const { theme, setTheme } = useThemeStore();
    const isDark =
        theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

    const [quickQuery, setQuickQuery] = useState('');

    return (
        <PageController route={Route}>
            <div className="mx-auto max-w-[1600px] space-y-6 p-6">
                {/* Header */}
                <div className="flex items-start justify-between">
                    <div>
                        <div className="mb-2 flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100">
                                <Bot size={24} className="text-accent-600" />
                            </div>
                            <Title level={2} className="!mb-0">
                                AI Chat Test Showcase
                            </Title>
                        </div>
                        <Paragraph className="max-w-2xl text-muted-foreground">
                            Test the General AI Chatbot tools and queries. Use the predefined test queries or type your
                            own to see how the AI assistant handles different scenarios. Monitor tool calls and results
                            in real-time.
                        </Paragraph>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-3 rounded-lg border border-separator bg-card px-4 py-2">
                            <Sun size={16} className={!isDark ? 'text-accent-500' : 'text-muted-foreground'} />
                            <Switch
                                checked={isDark}
                                onChange={(checked) => setTheme(checked ? 'dark' : 'light')}
                                checkedChildren={<Moon size={12} />}
                                unCheckedChildren={<Sun size={12} />}
                            />
                            <Moon size={16} className={isDark ? 'text-accent-500' : 'text-muted-foreground'} />
                        </div>
                    </div>
                </div>

                {/* Quick Test Grid */}
                <Card size="small">
                    <div className="mb-2 flex items-center gap-2">
                        <Zap size={14} className="text-gold-500" />
                        <Text strong className="text-sm">
                            Quick Tests
                        </Text>
                        <Text className="text-muted-foreground text-xs">- Click to start a test</Text>
                    </div>
                    <QuickTestGrid onSelectQuery={setQuickQuery} />
                </Card>

                {/* Main Chat Test Section */}
                <ChatTestSection key={quickQuery} />

                <Divider>
                    <div className="flex items-center gap-2 text-muted-foreground">
                        <Link2 size={14} />
                        <Text className="text-sm">API Reference</Text>
                    </div>
                </Divider>

                {/* API Reference */}
                <ApiReferenceSection />

                {/* Keyboard Shortcuts */}
                <Card size="small">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-6">
                            <div className="flex items-center gap-2">
                                <kbd className="rounded border border-separator bg-muted px-2 py-0.5 text-xs">
                                    Enter
                                </kbd>
                                <Text className="text-muted-foreground text-xs">Send message</Text>
                            </div>
                            <div className="flex items-center gap-2">
                                <kbd className="rounded border border-separator bg-muted px-2 py-0.5 text-xs">
                                    Shift + Enter
                                </kbd>
                                <Text className="text-muted-foreground text-xs">New line</Text>
                            </div>
                            <div className="flex items-center gap-2">
                                <kbd className="rounded border border-separator bg-muted px-2 py-0.5 text-xs">Tab</kbd>
                                <Text className="text-muted-foreground text-xs">Switch query category</Text>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <User size={14} className="text-muted-foreground" />
                            <Text className="text-muted-foreground text-xs">Testing as current authenticated user</Text>
                        </div>
                    </div>
                </Card>
            </div>
        </PageController>
    );
}
